import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, List, ListItem, ListItemAvatar,
  ListItemText, Avatar, Checkbox, Tab, Tabs, Box, Typography,
} from '@mui/material';


const NewConversationDialog = ({ open, onClose, onConversationCreated }) => {
  const currentUserId = useSelector((state) => state.user?._id);

  const [tab, setTab] = useState(0);        // 0 = Direct, 1 = Group
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (!open) return;
    api.get('/users/active?chat=true').then((res) => {
      setUsers(res.data.filter((u) => String(u._id) !== String(currentUserId)));
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.firstName?.toLowerCase().includes(q) ||
        u.lastName?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleToggle = (id) => {
    if (tab === 0) {
      setSelectedIds([id]);
    } else {
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      );
    }
  };

  const handleCreate = async () => {
    if (selectedIds.length === 0) return;
    try {
      setLoading(true);
      let res;
      if (tab === 0) {
        res = await api.post('/conversations/direct', { 
          targetUserId: selectedIds[0] 
        });
      } else {
        if (!groupName.trim()) return;
        res = await api.post('/conversations/group', {
          name: groupName.trim(),
          participantIds: selectedIds,
        });
      }
      onConversationCreated?.(res.data);
      handleClose();
    } catch {
      // silently ignore — parent handles errors
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearch('');
    setSelectedIds([]);
    setGroupName('');
    setTab(0);
    onClose();
  };
  

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>New Conversation</DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelectedIds([]); }} sx={{ mb: 1 }}>
          <Tab label="Direct" />
          <Tab label="Group" />
        </Tabs>

        {tab === 1 && (
          <TextField
            fullWidth size="small" variant="outlined"
            label="Group name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mb: 1 }}
          />
        )}

        <TextField
          fullWidth size="small" variant="outlined"
          placeholder="Search by name or email …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1 }}
        />

        <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
          {filtered.length === 0 ? (
            <Typography 
              variant="caption" 
              color="text.disabled" 
              sx={{ p: 1, display: 'block' }}
            >
              No users found
            </Typography>
          ) : (
            <List dense disablePadding>
              {filtered.map((u) => {
                const name = `${u.firstName} ${u.lastName}`;
                const initials = `${u.firstName?.[0] ?? ''}${u.lastName?.[0] ?? ''}`.toUpperCase();
                return (
                  <ListItem
                    key={u._id}
                    button
                    onClick={() => handleToggle(u._id)}
                    sx={{ borderRadius: 1 }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        src={u.picturePath || undefined} 
                        sx={{ width: 32, height: 32, fontSize: '0.75rem' }}
                      >
                        {initials}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={name}
                      secondary={u.email}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(u._id)}
                      onChange={() => handleToggle(u._id)}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} size="small">Cancel</Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleCreate}
          disabled={loading || selectedIds.length === 0 || (tab === 1 && !groupName.trim())}
        >
          {tab === 0 ? 'Open Chat' : 'Create Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewConversationDialog;
